import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProductType } from "../../api/model/common";
import { getQuotation, logSuccessfulQuotation } from "../../api/quotationApiClient";
import { ErrorMessageDialog } from "../../components/dialogs/ErrorMessageDialog";
import { MyArx2 } from "../../components/MyArx2";
import config, { Routes } from "../../config";
import { ProcessingContext } from "../../contexts/ProcessingContext";
import { Header } from "../../layout/Header";
import { IDictionary } from "../../model/types";
import { strings } from "../../strings/strings";
import { ErrorDialogType } from "../../utils/hooks/useErrorDialog";
import { useHotjar } from "../../utils/hooks/useHotjar";
import * as log from "../../utils/log";
import { LogFunctionalModule } from "../../utils/log";
import { EmtplCardSuccessfulQuotationView } from "./views/EmtplCardView";
import { GreenCardSuccessfulQuotationView } from "./views/GreenCardView";
import { TravelCardSuccessfulQuotationView } from "./views/TravelView";

import "./index.scss";

function sleep(delay = 0) {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    });
}

interface SuccessfulQuotationComponentProps {
    homeUrl: string;
    type: ProductType;
}

interface ProductData {
    unsuccessUrl: string;
    productHomeUrl: string;
    mainView: any;
    rigthView: any;
    functionalModule: LogFunctionalModule;
    getAnalitics: (quotation: any) => { event: string; email: string; phone: string };
    styleClass: string;
}

const expiredErrorCode = "AUTHORIZATION_EXPIRED";

const ProductDataMap: IDictionary<ProductData> = {
    [ProductType.EMTPL]: {
        unsuccessUrl: config.verification.unsuccessfulPaymentURL,
        productHomeUrl: config.mainPageUrl + Routes.emtplQuotationRoot,
        mainView: EmtplCardSuccessfulQuotationView,
        rigthView: MyArx2,
        functionalModule: LogFunctionalModule.STORE_EMTPL,
        styleClass: "",
        getAnalitics: (quotation: any) =>
            quotation.mtplContract.raxelPhone
                ? {
                      event: "webshop-emtpl-smart-drive-track-transaction",
                      email: quotation.mtplContract.primaryInsured.email,
                      phone: quotation.mtplContract.primaryInsured.mobileNumber,
                  }
                : {
                      event: "webshop-emtpl-track-transaction",
                      email: quotation.mtplContract.primaryInsured.email,
                      phone: quotation.mtplContract.primaryInsured.mobileNumber,
                  },
    },
    [ProductType.TRAVEL]: {
        unsuccessUrl: config.verification.travelUnsuccessfulPaymentURL,
        productHomeUrl: config.mainPageUrl + Routes.travelQuotationRoot,
        mainView: TravelCardSuccessfulQuotationView,
        rigthView: MyArx2,
        functionalModule: LogFunctionalModule.STORE_TRAVEL,
        styleClass: "",
        getAnalitics: (quotation: any) => ({
            event: "webshop-travel-track-transaction",
            email: quotation.travelWebContract.primaryInsured.email,
            phone: quotation.travelWebContract.primaryInsured.mobileNumber,
        }),
    },
    [ProductType.GREEN_CARD]: {
        unsuccessUrl: config.verification.greenUnsuccessfulPaymentURL,
        productHomeUrl: config.mainPageUrl + Routes.greenQuotationRoot,
        mainView: GreenCardSuccessfulQuotationView,
        rigthView: MyArx2,
        functionalModule: LogFunctionalModule.STORE_GREEN_CARD,
        styleClass: "green-card",
        getAnalitics: (quotation: any) => ({
            event: "green_card_transaction",
            email: quotation.mtplContract.primaryInsured.email,
            phone: quotation.mtplContract.primaryInsured.mobileNumber,
        }),
    },
};

export const SuccessfulQuotationComponent = ({
    homeUrl,
    type,
}: SuccessfulQuotationComponentProps) => {
    const [isLoading, setLoading] = useState(true);
    const [quotation, setQuotation] = useState<any>();
    const { quotationId } = useParams<any>();
    const [tryAgain, setTryAgain] = useState(true);
    const repeatTimeGetQuotation = 5000; //5 sec
    const [repeatCount, setRepeatCount] = useState(30);
    const { setProcessingOpen } = useContext(ProcessingContext);
    const productData = ProductDataMap[type];
    const specificStyleClass = productData.styleClass ? " " + productData.styleClass : "";
    const { trackHotjar } = useHotjar();
    const [errorMessage, setErrorMessage] = useState("");
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [errorRedirectUrl, setErrorRedirectUrl] = useState("");

    const fetch = async () => {
        setLoading(true);
        setProcessingOpen(true);
        const date = new Date();

        try {
            const responseQuotation = await getQuotation(quotationId, type);

            if (responseQuotation.status === "SIGNED") {
                setQuotation(responseQuotation);
                setLoading(false);
                setProcessingOpen(false);

                log.sendLog(
                    date,
                    productData.functionalModule,
                    "INFO",
                    "PAYMENT_SIGNED_WITH_AWAIT_TIME",
                    [{ quotation: quotation }],
                    "",
                );

                const { event, email, phone } = productData.getAnalitics(responseQuotation);

                console.log("Google Analytics Event = " + event, email, phone);

                trackHotjar("completed", type);
                logSuccessfulQuotation(quotationId, type);
                // @ts-ignore
                window.dataLayer.push({
                    event,
                    transactionID: quotationId,
                    transactionValue: responseQuotation.totalPremium,
                    email,
                    phone,
                });
            } else {
                setRepeatCount(repeatCount - 1);
                if (repeatCount <= 0) {
                    window.location.href = productData.unsuccessUrl + "/" + quotationId;
                    return;
                }
                await sleep(repeatTimeGetQuotation);
                setTryAgain(!tryAgain);
            }
        } catch (error: any) {
            setLoading(false);
            setProcessingOpen(false);
            const data = error?.response?.data;
            setErrorMessage(data?.errorMessage || strings.unkwonwnError);
            if (data?.errorCode === expiredErrorCode) {
                setErrorRedirectUrl(data?.redirectUrl || productData.productHomeUrl);
            }
            setOpenErrorDialog(true);
            log.sendLog(
                date,
                productData.functionalModule,
                "ERROR",
                "GET_QUOTATION_FAILED",
                [{ quotationId }],
                error?.message || "",
            );
        }
    };

    //for correct handle session
    useEffect(() => {
        localStorage.clear();
        sessionStorage.clear();
    }, []);

    useEffect(() => {
        fetch();
    }, [quotationId, tryAgain]);

    return (
        <div className={"successful-quotation" + specificStyleClass}>
            <div className={"successful-quotation__header"}>
                <Header homeUrl={homeUrl} type={type} />
            </div>

            <div className={"successful-quotation__content"}>
                <div className={"successful-quotation__content__left"}>
                    <div className={"successful-quotation__white"} />
                    {!isLoading &&
                        quotation &&
                        React.createElement(productData.mainView, {
                            quotation,
                            type,
                        })}
                </div>
                <div className={"successful-quotation__content__right"}>
                    {React.createElement(productData.rigthView)}
                </div>
            </div>

            <ErrorMessageDialog
                type={ErrorDialogType.ERROR}
                text={errorMessage}
                open={openErrorDialog}
                onClickListener={() => {
                    setOpenErrorDialog(false);
                    if (errorRedirectUrl) {
                        window.location.href = errorRedirectUrl;
                    }
                }}
            />
        </div>
    );
};
